import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class FolderPasswordService {
  private unlockedFolderIds = new Set<string>();
  private justUnlockedTimestamps = new Map<string, number>();
  private readonly UNLOCK_WINDOW_MS = 5000;

  isUnlocked(folderId: string): boolean {
    return this.unlockedFolderIds.has(folderId);
  }

  /**
   * Checks if folder was just unlocked (within the grace period).
   * This is for the navigation flow where password is checked once
   * but the component reloads and checks again.
   */
  wasJustUnlocked(folderId: string): boolean {
    const timestamp = this.justUnlockedTimestamps.get(folderId);
    if (!timestamp) return false;
 
    const now = Date.now();
    if (now - timestamp > this.UNLOCK_WINDOW_MS) {
      this.justUnlockedTimestamps.delete(folderId);
      return false;
    }
    return true;
  }
 
  /**
   * Mark folder as temporarily unlocked for navigation flow.
   * Unlike unlock(), this only lasts for a few seconds.
   */
  markJustUnlocked(folderId: string): void {
    this.justUnlockedTimestamps.set(folderId, Date.now());
  }

  unlock(folderId: string): void {
    this.unlockedFolderIds.add(folderId);
  }

  lock(folderId: string): void {
    this.unlockedFolderIds.delete(folderId);
  }

  async hashPassword(password: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(password);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
  }

  async verifyPassword(password: string, storedHash: string): Promise<boolean> {
    const hash = await this.hashPassword(password);
    return hash === storedHash;
  }
}
